export { columnsGen } from "./columns";
import { transformAllDataTables } from "../shared";

const initialColumnsArr = [
    ["pretty_name", true],
    ["instance_type", true],
    ["family", false],
    ["memory", true],
    ["ECU", false],
    ["vCPU", true],
    ["cores", false],
    ["memory_per_vcpu", false],
    ["GPU", false],
    ["GPU_model", false],
    ["GPU_memory", false],
    ["GPU_memory_per_gpu", false],
    ["compute_capability", false],
    ["FPGA", false],
    ["ECU_per_vcpu", false],
    ["physical_processor", false],
    ["clock_speed_ghz", false],
    ["intel_avx", false],
    ["intel_avx2", false],
    ["intel_avx512", false],
    ["intel_turbo", false],
    ["storage", true],
    ["warmed-up", false],
    ["trim-support", false],
    ["storage_read_iops", false],
    ["storage_write_iops", false],
    ["arch", false],
    ["network_performance", true],
    ["baseline_bandwidth_gbps", false],
    ["burst_bandwidth_gbps", false],
    ["ebs_baseline_bandwidth", false],
    ["ebs_baseline_throughput", false],
    ["ebs_baseline_iops", false],
    ["ebs_max_bandwidth", false],
    ["ebs_throughput", false],
    ["ebs_iops", false],
    ["ebs_as_nvme", false],
    ["maxips", false],
    ["maxenis", false],
    ["enhanced_networking", false],
    ["nitro_support", false],
    ["nitro_enclave_support", false],
    ["vpc_only", false],
    ["ipv6_support", false],
    ["placement_group_support", false],
    ["linux_virtualization_types", false],
    ["emr", false],
    ["availability_zones", false],
    ["cost-ondemand", true],
    ["cost-reserved", true],
    ["cost-spot-min", true],
    ["cost-spot-max", false],
    ["cost-ondemand-rhel", false],
    ["cost-reserved-rhel", false],
    ["cost-spot-min-rhel", false],
    ["cost-spot-max-rhel", false],
    ["cost-ondemand-sles", false],
    ["cost-reserved-sles", false],
    ["cost-spot-min-sles", false],
    ["cost-spot-max-sles", false],
    ["cost-ondemand-mswin", true],
    ["cost-reserved-mswin", true],
    ["cost-spot-min-mswin", false],
    ["cost-spot-max-mswin", false],
    ["cost-ondemand-dedicated", false],
    ["cost-reserved-dedicated", false],
    ["cost-ondemand-mswinSQLWeb", false],
    ["cost-reserved-mswinSQLWeb", false],
    ["cost-ondemand-mswinSQL", false],
    ["cost-reserved-mswinSQL", false],
    ["cost-ondemand-mswinSQLEnterprise", false],
    ["cost-reserved-mswinSQLEnterprise", false],
    ["cost-ondemand-linuxSQLWeb", false],
    ["cost-reserved-linuxSQLWeb", false],
    ["cost-ondemand-linuxSQL", false],
    ["cost-reserved-linuxSQL", false],
    ["cost-ondemand-linuxSQLEnterprise", false],
    ["cost-reserved-linuxSQLEnterprise", false],
    ["spot-interrupt-rate", false],
    ["cost-emr", false],
    ["cost-eks-auto-mode", false],
    ["generation", false],
    ["date_introduced", false],
    ["coremark_iterations_second", true],
    ["gpu_architectures", false],
    ["gpu_current_temp_avg_celsius", false],
    ["gpu_power_max_watts_avg", false],
    ["ffmpeg_used_cuda", false],
    ["ffmpeg_speed", false],
    ["ffmpeg_fps", true],
    ["gpu_power_draw_watts_avg", false],
    ["gpu_clocks_graphics_avg", false],
    ["gpu_clocks_sm_avg", false],
    ["gpu_clocks_memory_avg", false],
    ["gpu_clocks_video_avg", false],
    ["memory_speed", false],
    ["uses_numa_architecture", false],
    ["numa_node_count", false],
    ["max_numa_distance", false],
    ["core_count_per_numa_node", false],
    ["thread_count_per_numa_node", false],
    ["memory_per_numa_node_mb", false],
    ["l3_per_numa_node_mb", false],
    ["l3_shared", false],
    ["compute_family", true],
    ["cost-ondemand-rhelHA", false],
    ["cost-reserved-rhelHA", false],
    ["cost-spot-min-rhelHA", false],
    ["cost-spot-max-rhelHA", false],
    ["is_bare_metal", false],
    ["is_trunking_compatible", false],
    ["branch_interface", false],
    ["max_ecs_tasks", false],
    ["max_pods", false],
] as const;

export const initialColumnsValue: {
    [idx in (typeof initialColumnsArr)[number][0]]: boolean;
} = {} as any;
for (const [key, value] of initialColumnsArr) {
    initialColumnsValue[key] = value;
}

export function transformDataTables(dataTablesData: any) {
    return transformAllDataTables(initialColumnsArr, dataTablesData);
}

export function makePrettyNames<V>(
    makeColumnOption: (
        key: keyof typeof initialColumnsValue,
        label: string,
    ) => V,
) {
    return [
        makeColumnOption("pretty_name", "Name"),
        makeColumnOption("instance_type", "API Name"),
        makeColumnOption("family", "Instance Family"),
        makeColumnOption("compute_family", "Compute Family"),
        makeColumnOption("coremark_iterations_second", "CoreMark Score"),
        makeColumnOption("ffmpeg_fps", "FFmpeg FPS"),
        makeColumnOption("memory", "Memory"),
        makeColumnOption("memory_speed", "Memory Speed"),
        makeColumnOption("uses_numa_architecture", "Uses NUMA Architecture"),
        makeColumnOption("numa_node_count", "NUMA Node Count"),
        makeColumnOption("max_numa_distance", "Max NUMA Distance"),
        makeColumnOption(
            "core_count_per_numa_node",
            "Cores per NUMA Node (Avg)",
        ),
        makeColumnOption(
            "thread_count_per_numa_node",
            "Threads per NUMA Node (Avg)",
        ),
        makeColumnOption(
            "memory_per_numa_node_mb",
            "Memory per NUMA Node (Avg MB)",
        ),
        makeColumnOption(
            "l3_per_numa_node_mb",
            "L3 Cache per NUMA Node (Avg MB)",
        ),
        makeColumnOption("l3_shared", "L3 Cache Shared"),
        makeColumnOption("ECU", "Compute Units (ECU)"),
        makeColumnOption("vCPU", "vCPUs"),
        makeColumnOption("cores", "Cores"),
        makeColumnOption("memory_per_vcpu", "GiB of Memory per vCPU"),
        makeColumnOption("GPU", "GPUs"),
        makeColumnOption("GPU_model", "GPU model"),
        makeColumnOption("GPU_memory_per_gpu", "GPU memory (per GPU)"),
        makeColumnOption("GPU_memory", "GPU memory (total)"),
        makeColumnOption("compute_capability", "CUDA Compute Capability"),
        makeColumnOption("FPGA", "FPGAs"),
        makeColumnOption("ECU_per_vcpu", "ECU per vCPU"),
        makeColumnOption("physical_processor", "Physical Processor"),
        makeColumnOption("clock_speed_ghz", "Clock Speed(GHz)"),
        makeColumnOption("intel_avx", "Intel AVX"),
        makeColumnOption("intel_avx2", "Intel AVX2"),
        makeColumnOption("intel_avx512", "Intel AVX-512"),
        makeColumnOption("intel_turbo", "Intel Turbo"),
        makeColumnOption("storage", "Instance Storage"),
        makeColumnOption("warmed-up", "Instance Storage: already warmed-up"),
        makeColumnOption("trim-support", "Instance Storage: SSD TRIM Support"),
        makeColumnOption("storage_read_iops", "Instance Store: Read IOPS"),
        makeColumnOption("storage_write_iops", "Instance Store: Write IOPS"),
        makeColumnOption("arch", "Arch"),
        makeColumnOption("network_performance", "Network Performance"),
        makeColumnOption(
            "baseline_bandwidth_gbps",
            "Network: Baseline Bandwidth",
        ),
        makeColumnOption("burst_bandwidth_gbps", "Network: Burst Bandwidth"),
        makeColumnOption(
            "ebs_baseline_bandwidth",
            "EBS Optimized: Baseline Bandwidth",
        ),
        makeColumnOption(
            "ebs_baseline_throughput",
            "EBS Optimized: Baseline Throughput (128K)",
        ),
        makeColumnOption(
            "ebs_baseline_iops",
            "EBS Optimized: Baseline IOPS (16K)",
        ),
        makeColumnOption("ebs_max_bandwidth", "EBS Optimized: Max Bandwidth"),
        makeColumnOption(
            "ebs_throughput",
            "EBS Optimized: Max Throughput (128K)",
        ),
        makeColumnOption("ebs_iops", "EBS Optimized: Max IOPS (16K)"),
        makeColumnOption("ebs_as_nvme", "EBS Exposed as NVMe"),
        makeColumnOption("maxips", "Max IPs"),
        makeColumnOption("maxenis", "Max ENIs"),
        makeColumnOption("enhanced_networking", "Enhanced Networking"),
        makeColumnOption("nitro_support", "Nitro Support"),
        makeColumnOption("nitro_enclave_support", "Nitro Enclave Support"),
        makeColumnOption("is_bare_metal", "Bare metal"),
        makeColumnOption("is_trunking_compatible", "Trunking compatible"),
        makeColumnOption("branch_interface", "Branch interface"),
        makeColumnOption("max_ecs_tasks", "Max ECS Tasks"),
        makeColumnOption("max_pods", "Max Pods"),
        makeColumnOption("vpc_only", "VPC Only"),
        makeColumnOption("ipv6_support", "IPv6 Support"),
        makeColumnOption("placement_group_support", "Placement Group Support"),
        makeColumnOption("linux_virtualization_types", "Linux Virtualization"),
        makeColumnOption("emr", "On EMR"),
        makeColumnOption("availability_zones", "Availability Zones"),
        makeColumnOption("cost-ondemand", "On Demand"),
        makeColumnOption("cost-reserved", "Linux Reserved cost"),
        makeColumnOption("cost-spot-min", "Linux Spot Minimum cost"),
        makeColumnOption("cost-spot-max", "Linux Spot Average cost"),
        makeColumnOption("cost-ondemand-rhel", "RHEL On Demand cost"),
        makeColumnOption("cost-reserved-rhel", "RHEL Reserved cost"),
        makeColumnOption("cost-spot-min-rhel", "RHEL Spot Minimum cost"),
        makeColumnOption("cost-spot-max-rhel", "RHEL Spot Maximum cost"),
        makeColumnOption("cost-ondemand-rhelHA", "RHEL with HA On Demand cost"),
        makeColumnOption("cost-reserved-rhelHA", "RHEL with HA Reserved cost"),
        makeColumnOption(
            "cost-spot-min-rhelHA",
            "RHEL with HA Spot Minimum cost",
        ),
        makeColumnOption(
            "cost-spot-max-rhelHA",
            "RHEL with HA Spot Maximum cost",
        ),
        makeColumnOption("cost-ondemand-sles", "SLES On Demand cost"),
        makeColumnOption("cost-reserved-sles", "SLES Reserved cost"),
        makeColumnOption("cost-spot-min-sles", "SLES Spot Minimum cost"),
        makeColumnOption("cost-spot-max-sles", "SLES Spot Maximum cost"),
        makeColumnOption("cost-ondemand-mswin", "Windows On Demand cost"),
        makeColumnOption("cost-reserved-mswin", "Windows Reserved cost"),
        makeColumnOption("cost-spot-min-mswin", "Windows Spot Minimum cost"),
        makeColumnOption("cost-spot-max-mswin", "Windows Spot Average cost"),
        makeColumnOption("cost-ondemand-dedicated", "Dedicated Host On Demand"),
        makeColumnOption("cost-reserved-dedicated", "Dedicated Host Reserved"),
        makeColumnOption(
            "cost-ondemand-mswinSQLWeb",
            "Windows SQL Web On Demand cost",
        ),
        makeColumnOption(
            "cost-reserved-mswinSQLWeb",
            "Windows SQL Web Reserved cost",
        ),
        makeColumnOption(
            "cost-ondemand-mswinSQL",
            "Windows SQL Std On Demand cost",
        ),
        makeColumnOption(
            "cost-reserved-mswinSQL",
            "Windows SQL Std Reserved cost",
        ),
        makeColumnOption(
            "cost-ondemand-mswinSQLEnterprise",
            "Windows SQL Ent On Demand cost",
        ),
        makeColumnOption(
            "cost-reserved-mswinSQLEnterprise",
            "Windows SQL Ent Reserved cost",
        ),
        makeColumnOption(
            "cost-ondemand-linuxSQLWeb",
            "Linux SQL Web On Demand cost",
        ),
        makeColumnOption(
            "cost-reserved-linuxSQLWeb",
            "Linux SQL Web Reserved cost",
        ),
        makeColumnOption(
            "cost-ondemand-linuxSQL",
            "Linux SQL Std On Demand cost",
        ),
        makeColumnOption(
            "cost-reserved-linuxSQL",
            "Linux SQL Std Reserved cost",
        ),
        makeColumnOption(
            "cost-ondemand-linuxSQLEnterprise",
            "Linux SQL Ent On Demand cost",
        ),
        makeColumnOption(
            "cost-reserved-linuxSQLEnterprise",
            "Linux SQL Ent Reserved cost",
        ),
        makeColumnOption(
            "spot-interrupt-rate",
            "Linux Spot Interrupt Frequency",
        ),
        makeColumnOption("cost-emr", "EMR cost"),
        makeColumnOption("cost-eks-auto-mode", "EKS Auto Mode cost"),
        makeColumnOption("generation", "Generation"),
        makeColumnOption("date_introduced", "Date Introduced"),
        makeColumnOption("gpu_architectures", "GPU Architectures"),
        makeColumnOption("gpu_current_temp_avg_celsius", "GPU Temp (Avg °C)"),
        makeColumnOption("ffmpeg_used_cuda", "FFmpeg Used CUDA"),
        makeColumnOption("ffmpeg_speed", "FFmpeg Speed"),
        makeColumnOption(
            "gpu_power_draw_watts_avg",
            "GPU Power Draw (Avg Watts)",
        ),
        makeColumnOption(
            "gpu_power_max_watts_avg",
            "GPU Power Max (Avg Watts)",
        ),
        makeColumnOption(
            "gpu_clocks_graphics_avg",
            "GPU Graphics Clock (Avg MHz)",
        ),
        makeColumnOption("gpu_clocks_sm_avg", "GPU SM Clock (Avg MHz)"),
        makeColumnOption("gpu_clocks_memory_avg", "GPU Memory Clock (Avg MHz)"),
        makeColumnOption("gpu_clocks_video_avg", "GPU Video Clock (Avg MHz)"),
    ];
}
